import React, { useEffect, useMemo, useState, memo } from 'react';
import { Chart } from 'react-google-charts';
import './NewDatabaseGraphPanel.css';

interface NewDatabaseGraphPanelProps {
    activeTable: string;
    tableData: any[];
    isTransitioning?: boolean;
}

const NewDatabaseGraphPanel: React.FC<NewDatabaseGraphPanelProps> = ({ activeTable, tableData, isTransitioning = false }) => {
    const [selectedChartType, setSelectedChartType] = useState<'summary_chart' | 'projection_chart' | 'histogram_chart'>('summary_chart');
    const [yAxis, setYAxis] = useState<'gross_profit' | 'gross_barrels' | 'net_barrels' | 'margin'>('gross_profit');
    const [dataSeries, setDataSeries] = useState<'bsw' | 'gross_barrels' | 'gravity'>('bsw');
    const [chartSnapshot, setChartSnapshot] = useState<string | null>(null);
    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    const convertYAxisName = (name: string): string => {
        switch (name) {
            case 'gross_profit': return 'Gross Profit';
            case 'gross_barrels': return 'Gross Barrels';
            case 'margin': return 'Margin';
            case 'net_barrels': return 'Net Barrels';
            default: return 'Unknown';
        }
    };

    const convertHAxisName = (name: string): string => {
        switch (name) {
            case 'bsw': return 'BS&W (%)';
            case 'gross_barrels': return 'Gross Barrels';
            case 'gravity': return 'Corrected Gravity';
            default: return 'Unknown';
        }
    };

    // Capture snapshot when transition starts
    useEffect(() => {
        if (isTransitioning && chartContainerRef.current && !chartSnapshot) {
            const chartElement = chartContainerRef.current.querySelector('svg');
            if (chartElement) {
                try {
                    const svgData = new XMLSerializer().serializeToString(chartElement);
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const img = new Image();
                    
                    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(svgBlob);
                    
                    img.onload = () => {
                        canvas.width = chartElement.clientWidth;
                        canvas.height = chartElement.clientHeight;
                        ctx?.drawImage(img, 0, 0);
                        const dataUrl = canvas.toDataURL('image/png');
                        setChartSnapshot(dataUrl);
                        URL.revokeObjectURL(url);
                    };
                    
                    img.src = url;
                } catch (error) {
                    console.error('Failed to capture chart snapshot:', error);
                }
            }
        } else if (!isTransitioning) {
            // Clear snapshot when transition ends
            setChartSnapshot(null);
        }
    }, [isTransitioning, chartSnapshot]);

    const BarChartData = memo(() => {
        const [chartData, setChartData] = useState<(string | number | null)[][]>([['Producer', 'Gross Profit']]);
        
        const chartOptions = useMemo(() => ({
            title: 'Summary Chart',
            hAxis: { title: 'Producer' },
            legend: 'none',
            colors: ['#4285F4'],
            animation: {
                duration: 0
            }
        }), []);

        useEffect(() => {
            if (!tableData?.length) {
                setChartData([['Producer', 'Gross Profit'], [0, 0]]);
                return;
            }

            const grouped = tableData.reduce<Record<string, { net_profit: number, gross_barrels: number, net_barrels: number }>>((acc, item) => {
                const producer = item.producer || 'Unknown';
                const net_profit = Number(item.net_profit) || 0;
                const gross_barrels = Number(item.gross_barrels) || 0;
                const net_barrels = Number(item.net_barrels) || 0;
                if (!acc[producer]) acc[producer] = { net_profit: 0, gross_barrels: 0, net_barrels: 0 };
                acc[producer].net_profit += net_profit;
                acc[producer].gross_barrels += gross_barrels;
                acc[producer].net_barrels += net_barrels;
                return acc;
            }, {});

            let data: (string | number)[][];
            switch (yAxis) {
                case 'gross_barrels':
                    data = [
                        ['Producer', 'Gross Barrels'],
                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].gross_barrels - a[1].gross_barrels)
                            .map(([producer, vals]) => [producer, vals.gross_barrels])
                    ];
                    break;
                case 'net_barrels':
                    data = [
                        ['Producer', 'Net Barrels'],
                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].net_barrels - a[1].net_barrels)
                            .map(([producer, vals]) => [producer, vals.net_barrels])
                    ];
                    break;
                case 'margin':
                    data = [
                        ['Producer', 'Margin'],
                        ...Object.entries(grouped)
                            .sort((a, b) => {
                                const marginA = a[1].net_barrels ? a[1].net_profit / a[1].net_barrels : 0;
                                const marginB = b[1].net_barrels ? b[1].net_profit / b[1].net_barrels : 0;
                                return marginB - marginA;
                            })
                            .map(([producer, vals]) => [
                                producer,
                                vals.net_barrels ? vals.net_profit / vals.net_barrels : 0
                            ])
                    ];
                    break;
                default:
                    data = [
                        ['Producer', 'Gross Profit'],
                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].net_profit - a[1].net_profit)
                            .map(([producer, vals]) => [producer, vals.net_profit])
                    ];
            }

            setChartData(data);
        }, [tableData, yAxis]);

        const vAxisTitle = convertYAxisName(yAxis);

        return (
            <Chart
                chartType='ColumnChart'
                width='100%'
                height='100%'
                data={chartData}
                options={{
                    ...chartOptions,
                    vAxis: { title: vAxisTitle }
                }}
            />
        );
    });

    const HistogramChart = memo(() => {
        const [chartData, setChartData] = useState<(string | number)[][]>([['BS&W']]);
        
        const chartOptions = useMemo(() => ({
            title: 'Distribution Chart',
            legend: { position: 'none' },
            colors: ['#4285F4'],
            animation: {
                duration: 0
            }
        }), []);

        useEffect(() => {
            if (!tableData?.length) {
                setChartData([['BS&W'], [0]]);
                return;
            }

            let data: (string | number)[][];
            switch (dataSeries) {
                case 'bsw':
                    data = [['BS&W'], ...tableData.map((item: any) => [Number(item.bsw) || 0])];
                    break;
                case 'gross_barrels':
                    data = [['Gross Barrels'], ...tableData.map((item: any) => [Number(item.gross_barrels) || 0])];
                    break;
                case 'gravity':
                    data = [['Gravity'], ...tableData.map((item: any) => [Number(item.corrected_gravity) || 0])];
                    break;
                default:
                    data = [['BS&W'], ...tableData.map((item: any) => [Number(item.bsw) || 0])];
            }
            setChartData(data);
        }, [tableData, dataSeries]);

        const hAxisTitle = convertHAxisName(dataSeries);

        return (
            <Chart
                chartType='Histogram'
                width='100%'
                height='100%'
                data={chartData}
                options={{
                    ...chartOptions,
                    hAxis: { title: hAxisTitle },
                    vAxis: { title: 'Count' },
                    histogram: { bucketSize: dataSeries === 'gross_barrels' ? 10 : 1 }
                }}
            />
        );
    });

    return (
        <div className={`newdb-sidePanel-graphContainer ${isTransitioning ? 'transitioning' : ''}`}>
            <div className="newdb-sidePanel-graphContent">
                <div className="newdb-graph-controls">
                    <label>
                        Chart Type:
                        <select value={selectedChartType} onChange={e => setSelectedChartType(e.target.value as typeof selectedChartType)}>
                            <option value="summary_chart">Summary Chart</option>
                            <option value="histogram_chart">Histogram Chart</option>
                        </select>
                    </label>
                    
                    {selectedChartType === 'summary_chart' ? (
                        <label>
                            Y-Axis:
                            <select value={yAxis} onChange={e => setYAxis(e.target.value as typeof yAxis)}>
                                <option value="gross_profit">Gross Profit</option>
                                <option value="gross_barrels">Gross Barrels</option>
                                <option value="net_barrels">Net Barrels</option>
                                <option value="margin">Margin</option>
                            </select>
                        </label>
                    ) : (
                        <label>
                            Data Series:
                            <select value={dataSeries} onChange={e => setDataSeries(e.target.value as typeof dataSeries)}>
                                <option value="bsw">BS&W</option>
                                <option value="gross_barrels">Gross Barrels</option>
                                <option value="gravity">Gravity</option>
                            </select>
                        </label>
                    )}
                </div>

                <div className="newdb-graph-chart" ref={chartContainerRef}>
                    {isTransitioning && chartSnapshot && (
                        <div className="newdb-graph-snapshot-overlay">
                            <img 
                                src={chartSnapshot} 
                                alt="Chart snapshot"
                                className="newdb-graph-snapshot-image"
                            />
                        </div>
                    )}
                    <div className={`newdb-graph-chart-wrapper ${isTransitioning ? 'hidden' : ''}`}>
                        {selectedChartType === 'summary_chart' ? (
                            <BarChartData />
                        ) : (
                            <HistogramChart />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewDatabaseGraphPanel;
